import { useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import { DesignerMatchingProfile, BudgetRange, AgeRange } from "../../__generated__/types";
import { bugetRangeLabels, ageRangeLabels } from "../../utils/option-helpers";

const toTitleCase = (value?: string | null) => {
  if (!value) return "";
  return value
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
};

const formatRankedItems = <T extends {
  ranking?: number | null;
  category?: string | null;
  experience?: string | null;
  strength?: string | null;
  partnershipStyle?: string | null;
  acquisitionPreference?: string | null;
  style?: string | null;
}>(items: Array<T | null | undefined>) => {
  if (!items || items.length === 0) return [];
  return items
    .filter((item): item is T => !!item)
    .map((item) => {
      const label =
        item.category ||
        item.experience ||
        item.strength ||
        item.partnershipStyle ||
        item.acquisitionPreference ||
        item.style ||
        "";
      return item?.ranking
        ? `${item.ranking}. ${toTitleCase(label)}`
        : toTitleCase(label);
    })
    .filter(Boolean);
};

type SectionItem = {
  label?: string; // Optional label for grouped items (e.g., "Project Types:", "Experience:")
  value: string; // The actual value to display
};

type Section = {
  title: string;
  items: SectionItem[]; // Changed from string[] to SectionItem[]
};

const buildSections = (profile?: DesignerMatchingProfile | null) => {
  
  if (!profile) {
    return [];
  }

  const sections: Section[] = [];

  if (profile.personalityType) {
    sections.push({
      title: "Personality",
      items: [{ value: toTitleCase(profile.personalityType) }],
    });
  } else {
    console.log('buildSections - No personalityType found');
  }

  if (profile.expertise) {
    
    const expertiseItems: SectionItem[] = [];
    // Handle API typo: use either competency or compentency
    const rawCompetency = (profile.expertise as any)?.competency ?? (profile.expertise as any)?.compentency ?? {};
    
    // Project Types - each as separate chip
    if (profile.expertise.projectTypes?.length) {
      profile.expertise.projectTypes.forEach((pt) => {
        if (pt) {
          expertiseItems.push({ label: "Project Types", value: toTitleCase(pt) });
        }
      });
    } else {
      console.log('buildSections - No projectTypes found in expertise');
    }

    // Experience - each as separate chip
    const experience = profile.expertise.experience?.map((exp) => toTitleCase(exp || "")).filter(Boolean);
    if (experience && experience.length) {
      experience.forEach((exp) => {
        expertiseItems.push({ label: "Experience", value: exp });
      });
    } else {
      console.log('buildSections - No experience found in expertise');
    }

    // Strengths Ranking - each as separate chip
    const strengths = formatRankedItems(rawCompetency?.strengths || []);
    if (strengths.length) {
      strengths.forEach((strength) => {
        expertiseItems.push({ label: "Strengths Ranking", value: strength });
      });
    } else {
      console.log('buildSections - No strengths found in expertise');
    }

    // Competency - each as separate chip
    const { __typename, strengths: _, ...competencyLevels } = rawCompetency as any;
    const competencyEntries = Object.entries(competencyLevels).filter(
      ([key, level]) => level && level !== "None"
    );
    if (competencyEntries.length) {
      competencyEntries.forEach(([key, level]) => {
        expertiseItems.push({ 
          label: "Competency", 
          value: `${toTitleCase(key)} (${toTitleCase(String(level))})` 
        });
      });
    } else {
      console.log('buildSections - No competency found in expertise');
    }

    if (expertiseItems.length) {
      sections.push({
        title: "Expertise",
        items: expertiseItems,
      });
    } else {
      console.warn('buildSections - Expertise exists but no items were added');
    }
  } else {
    console.log('buildSections - No expertise found in profile');
  }

  if (profile.style) {
    const styleItems: SectionItem[] = [];
    if (profile.style.anyStyle !== undefined) {
      styleItems.push({
        value: profile.style.anyStyle
          ? "Can design any style"
          : "Prefers specific styles"
      });
    }
    if (profile.style.knownForStyle) {
      styleItems.push({ label: "Known For", value: toTitleCase(profile.style.knownForStyle) });
    }
    // Strong Styles - each as separate chip
    if (profile.style.strongStyles?.length) {
      profile.style.strongStyles.forEach((style) => {
        if (style) {
          styleItems.push({ label: "Strong Styles", value: toTitleCase(style) });
        }
      });
    }
    // Style Rankings - each as separate chip
    const styleRankings = formatRankedItems(profile.style.styleRankings || []);
    if (styleRankings.length) {
      styleRankings.forEach((ranking) => {
        styleItems.push({ label: "Style Rankings", value: ranking });
      });
    }

    if (styleItems.length) {
      sections.push({
        title: "Design Style",
        items: styleItems,
      });
    }
  }

  if (profile.workStyle) {
    const workItems: SectionItem[] = [];
    if (profile.workStyle.partnershipOptions?.length) {
      profile.workStyle.partnershipOptions.forEach((option) => {
        if (option) {
          workItems.push({ label: "Partnership Options", value: toTitleCase(option) });
        }
      });
    }
    if (profile.workStyle.compensationModel?.length) {
      profile.workStyle.compensationModel.forEach((model) => {
        if (model) {
          workItems.push({ label: "Compensation Models", value: toTitleCase(model) });
        }
      });
    }
    if (profile.workStyle.presentationType?.length) {
      profile.workStyle.presentationType.forEach((type) => {
        if (type) {
          workItems.push({ label: "Presentation Types", value: toTitleCase(type) });
        }
      });
    }
    if (profile.workStyle.minimumBudget) {
      // Use budget range formatter to display user-friendly budget range
      const budgetLabel = bugetRangeLabels[profile.workStyle.minimumBudget as BudgetRange] || toTitleCase(profile.workStyle.minimumBudget);
      workItems.push({ label: "Minimum Budget", value: budgetLabel });
    }
    if (workItems.length) {
      sections.push({
        title: "Work Style",
        items: workItems,
      });
    }
  }

  if (profile.preferences) {
    const preferenceItems: SectionItem[] = [];
    if (profile.preferences.social?.pronouns) {
      preferenceItems.push({ label: "Pronouns", value: toTitleCase(profile.preferences.social.pronouns) });
    }
    // Handle pets: can be array (old format) or string (new format)
    const petsValue = profile.preferences.social?.pets;
    if (petsValue !== undefined && petsValue !== null) {
      // Map enum values to friendly labels
      const petLabelMap: Record<string, string> = {
        dog: 'I live with a dog',
        cat: 'I live with a cat',
        fish: 'I live with fish or aquatic pets',
        none: "I prefer not to design for homes with pets",
        // Legacy keys for backward compatibility
        liveWithDog: 'I live with a dog',
        liveWithCat: 'I live with a cat',
        liveWithFishOrAquatic: 'I live with fish or aquatic pets',
        noPetsButComfortable: "I'm comfortable designing for pet-friendly homes",
        noPets: "I prefer not to design for homes with pets",
      };
      
      let petDisplay: string;
      if (Array.isArray(petsValue)) {
        // Old format: array
        petDisplay = petsValue
          .map((pet) => petLabelMap[pet || ""] || toTitleCase(pet || ""))
          .filter(Boolean)
          .join(", ");
      } else {
        // New format: single string
        petDisplay = petLabelMap[petsValue] || toTitleCase(petsValue);
      }
      
      if (petDisplay) {
        preferenceItems.push({ label: "Pet Experience", value: petDisplay });
      }
    }
    if (profile.preferences.social?.lgbtqPositive !== null && profile.preferences.social?.lgbtqPositive !== undefined) {
      preferenceItems.push({ label: "LGBTQ Positive", value: profile.preferences.social.lgbtqPositive ? 'Yes' : 'No' });
    }
    if (profile.preferences.social?.womenOwned !== null && profile.preferences.social?.womenOwned !== undefined) {
      preferenceItems.push({ label: "Women-Owned Business", value: profile.preferences.social.womenOwned ? 'Yes' : 'No' });
    }
    if (profile.preferences.social?.minorityOwned !== undefined && profile.preferences.social?.minorityOwned !== null) {
      const minorityOwnedValue = typeof profile.preferences.social.minorityOwned === 'boolean'
        ? (profile.preferences.social.minorityOwned ? 'Yes' : 'No')
        : toTitleCase(String(profile.preferences.social.minorityOwned));
      preferenceItems.push({ label: "Minority-Owned", value: minorityOwnedValue });
    }
    if (profile.preferences.social?.preferToWorkWithRace) {
      // Map enum values to friendly labels
      const raceLabelMap: Record<string, string> = {
        'Asian': 'Asian',
        'BlackOrAfricanAmerican': 'Black or African American',
        'Caucasian': 'Caucasian',
        'PacificIslander': 'Pacific Islander',
        'LatinxOrHispanic': 'Latinx or Hispanic',
        'NativeAmerican': 'Native American',
        'Indian': 'Indian',
        'noPreference': 'No Preference'
      };
      const raceValue = profile.preferences.social.preferToWorkWithRace;
      const raceDisplay = raceLabelMap[raceValue] || toTitleCase(raceValue);
      preferenceItems.push({ label: "What's your race", value: raceDisplay });
    }
    if (profile.preferences.social?.politicalAffiliation) {
      const politicalLabel = profile.preferences.social.politicalAffiliation === 'other' && profile.preferences.social.otherPoliticalAffiliation
        ? profile.preferences.social.otherPoliticalAffiliation
        : toTitleCase(profile.preferences.social.politicalAffiliation);
      preferenceItems.push({ label: "Political Affiliation", value: politicalLabel });
    }
    if (profile.preferences.social?.preferSamePolitics !== null && profile.preferences.social?.preferSamePolitics !== undefined) {
      preferenceItems.push({ label: "Prefers Same Politics", value: profile.preferences.social.preferSamePolitics ? 'Yes' : 'No' });
    }
    if (profile.preferences.zipCodes?.length) {
      profile.preferences.zipCodes.filter(Boolean).forEach((zipCode) => {
        if (zipCode) {
          const [zipPart, cityPart] = String(zipCode).split('::');
          const display = cityPart ? `${zipPart} (${cityPart})` : zipPart;
          preferenceItems.push({ label: "Service Area", value: display });
        }
      });
    }
    if (profile.preferences.ageRange) {
      // Use age range formatter to display user-friendly age range
      // This represents the designer's age range, not preferred client age range
      const ageLabel = ageRangeLabels[profile.preferences.ageRange as AgeRange] || toTitleCase(profile.preferences.ageRange);
      preferenceItems.push({ label: "Designer Age Range", value: ageLabel });
    }
    if (profile.preferences.children !== null && profile.preferences.children !== undefined) {
      preferenceItems.push({ label: "Do you have children", value: profile.preferences.children ? 'Yes' : 'No' });
    }
    if (profile.preferences.acquisitionPreferences?.length) {
      profile.preferences.acquisitionPreferences.forEach((pref) => {
        if (pref) {
          preferenceItems.push({ label: "Acquisition Preferences", value: toTitleCase(pref) });
        }
      });
    }
    if (preferenceItems.length) {
      sections.push({
        title: "Designer Preferences",
        items: preferenceItems,
      });
    }
  }

  return sections;
};

type SectionType = 'personality' | 'expertise' | 'design-style' | 'work-style' | 'preferences' | 'all';

interface DesignerMatchingProfileRetakeSummaryProps {
  profile?: DesignerMatchingProfile | null;
  loading?: boolean;
  currentSection?: SectionType; // Filter to show only this section's answers, or 'all' for summary screen
}

export const DesignerMatchingProfileRetakeSummary = ({
  profile,
  loading,
  currentSection = 'all', // Default to 'all' to show all sections (for summary screen)
}: DesignerMatchingProfileRetakeSummaryProps) => {

  const sections = useMemo(() => {
    
    const builtSections = buildSections(profile);
    
    // Filter sections based on currentSection
    if (currentSection === 'all') {
      // Show all sections (for summary screen)
      return builtSections;
    }
    
    // Map section names to filter
    const sectionNameMap: Record<SectionType, string> = {
      'personality': 'Personality',
      'expertise': 'Expertise',
      'design-style': 'Design Style',
      'work-style': 'Work Style',
      'preferences': 'Designer Preferences',
      'all': 'All'
    };
    
    const targetSectionName = sectionNameMap[currentSection];
    const filteredSections = builtSections.filter(section => section.title === targetSectionName);
    
    
    
    return filteredSections;
  }, [profile, currentSection]);

  if (loading && !profile) {
    return (
      <Box sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading your previous answers…
        </Typography>
      </Box>
    );
  }

  if (!profile) {
    return null;
  }
  

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {sections.map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {section.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {section.items.map((item, index) => (
                <Chip
                  key={`${section.title}-${index}`}
                  label={item.label ? `${item.label}: ${item.value}` : item.value}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};